import {
  ID,
  IDRead,
  IDCreate,
  UserRequest,
  UserResponse,
  makeEnum, 
} from '../spec-generators/api-types-helpers.js';
export const Status = makeEnum({
  concept: 'concept',
  active: 'active',
  finished: 'finished',
  published: 'published',
  archived: 'archived',
  former: 'former',
});
type Status = typeof Status[keyof typeof Status];
export const DBLogType = makeEnum({
  INSERT: 'INSERT',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
});
type DBLogType = typeof DBLogType[keyof typeof DBLogType];
export const Role = makeEnum({
  user: 'user',
  manager: 'manager',
  admin: 'admin',
});
type Role = typeof Role[keyof typeof Role];
export type API = {
  ApplicationLog: {
    delete: {};
    create: {
      message: any;
      timestamp: number;
    };
    read: {
      message?: any;
      timestamp?: number;
      id?: number;
    };
    update: {
      message?: any;
      timestamp?: number;
    };
  };

  ApplicationSettings: {
    create: {
      tools?: any | null;
      rights?: any | null;
      maxmbs?: any | null;
      config?: any | null;
    };
    read: {
      tools?: any | null;
      rights?: any | null;
      maxmbs?: any | null;
      config?: any | null;
      id?: number;
    };
    update: {
      tools?: any | null;
      rights?: any | null;
      maxmbs?: any | null;
      config?: any | null;
    };
  };

  File: {
    delete: {};
    create: {
      description: string;
      file_path: string;
      fileName: string;
      uri: string;
      storage: string;
      size: number;
      mimeType: string;
      uploadTime?: Date | null;
      folder?: number | null;
      sectionFiles: number[];
      avatarsFor: number[];
      projectImageFor: number[];
      publicationImageFor: number[];
      memberImageFor: number[];
    };
    read: {
      description?: string;
      file_path?: string;
      fileName?: string;
      author?: API['User']['read'];
      uri?: string;
      size?: number;
      mimeType?: string;
      uploadTime?: Date | null;
      folder?: API['Folder']['read'] | null;
      sectionFiles?: API['Section']['read'][];
      avatarsFor?: API['User']['read'][];
      projectImageFor?: API['Project']['read'][];
      publicationImageFor?: API['Publication']['read'][];
      memberImageFor?: API['Member']['read'][];
      id?: number;
    };
    update: {
      description?: string;
      file_path?: string;
      fileName?: string;
      uri?: string;
      storage?: string;
      size?: number;
      mimeType?: string;
      uploadTime?: Date | null;
      folder?: number | null;
      sectionFiles?: number[];
      avatarsFor?: number[];
      projectImageFor?: number[];
      publicationImageFor?: number[];
      memberImageFor?: number[];
    };
  };

  User: {
    delete: {};
    create: {
      username: string;
      email?: string | null;
      roles: Role[];
      locale?: string | null;
      noTranslate?: string | null;
      password?: string | null;
      about: string;
      avatar?: number | null;
      uploadedFiles: number[];
      pages: number[];
      projects: number[];
      publications: number[];
      member?: number | null;
    };
    read: {
      username?: string;
      email?: string | null;
      roles?: Role[];
      locale?: string | null;
      noTranslate?: string | null;
      isVerified?: boolean;
      failedLogins?: number | null;
      lockedUntil?: Date | null;
      lastLogin?: Date | null;
      createdAt?: Date;
      about?: string;
      avatar?: API['File']['read'] | null;
      uploadedFiles?: API['File']['read'][];
      pages?: API['Page']['read'][];
      projects?: API['Project']['read'][];
      publications?: API['Publication']['read'][];
      member?: API['Member']['read'] | null;
      logs?: API['Log']['read'][];
      id?: number;
    };
    update: {
      username?: string;
      email?: string | null;
      roles?: Role[];
      locale?: string | null;
      noTranslate?: string | null;
      isVerified?: boolean;
      password?: string | null;
      failedLogins?: number | null;
      lockedUntil?: Date | null;
      about?: string;
      avatar?: number | null;
      uploadedFiles?: number[];
      pages?: number[];
      projects?: number[];
      publications?: number[];
      member?: number | null;
    };
  };

  Page: {
    delete: {};
    create: {
      author: number;
      title: string;
      slug: string;
      sections: number[];
      locale: string;
    };
    read: {
      author?: API['User']['read'];
      title?: string;
      slug?: string;
      sections?: API['Section']['read'][];
      locale?: string;
      id?: number;
    };
    update: {
      author?: number;
      title?: string;
      slug?: string;
      sections?: number[];
      locale?: string;
    };
  };

  Section: {
    delete: {};
    create: {
      text: any;
      files: number[];
      page: number;
      farey_order: string;
      contenttype?: string | null;
      content?: number | null;
    };
    read: {
      text?: any;
      files?: API['File']['read'][];
      page?: API['Page']['read'];
      farey_order?: string;
      contenttype?: string | null;
      content?: number | null;
      id?: number;
    };
    update: {
      text?: any;
      files?: number[];
      page?: number;
      farey_order?: string;
      contenttype?: string | null;
      content?: number | null;
    };
  };

  Project: {
    delete: {};
    create: {
      author: number;
      title_en: string;
      title_nl: string;
      text_en: string;
      text_nl: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order: string;
      startDate: Date;
      endDate?: Date | null;
      duration: string;
      status: Status;
      publications: number[];
      members: number[];
    };
    read: {
      author?: API['User']['read'];
      title_en?: string;
      title_nl?: string;
      text_en?: string;
      text_nl?: string;
      image?: API['File']['read'] | null;
      image_contain?: boolean | null;
      farey_order?: string;
      startDate?: Date;
      endDate?: Date | null;
      duration?: string;
      status?: Status;
      publications?: API['Publication']['read'][];
      members?: API['Member']['read'][];
      id?: number;
    };
    update: {
      author?: number;
      title_en?: string;
      title_nl?: string;
      text_en?: string;
      text_nl?: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order?: string;
      startDate?: Date;
      endDate?: Date | null;
      duration?: string;
      status?: Status;
      publications?: number[];
      members?: number[];
    };
  };

  Publication: {
    delete: {};
    create: {
      author: number;
      title: string;
      text: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order: string;
      publicationDate: Date;
      status: Status;
      download?: string | null;
      project?: number | null;
      members: number[];
    };
    read: {
      author?: API['User']['read'];
      title?: string;
      text?: string;
      image?: API['File']['read'] | null;
      image_contain?: boolean | null;
      farey_order?: string;
      publicationDate?: Date;
      status?: Status;
      download?: string | null;
      project?: API['Project']['read'] | null;
      members?: API['Member']['read'][];
      id?: number;
    };
    update: {
      author?: number;
      title?: string;
      text?: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order?: string;
      publicationDate?: Date;
      status?: Status;
      download?: string | null;
      project?: number | null;
      members?: number[];
    };
  };

  Member: {
    delete: {};
    create: {
      user?: number | null;
      name: string;
      firstname: string;
      text_en: string;
      text_nl: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order: string;
      startDate: Date;
      endDate?: Date | null;
      status: Status;
      function_en?: string | null;
      function_nl?: string | null;
      linkedin?: string | null;
      orcid?: string | null;
      publications: number[];
      projects: number[];
    };
    read: {
      user?: API['User']['read'] | null;
      name?: string;
      firstname?: string;
      text_en?: string;
      text_nl?: string;
      image?: API['File']['read'] | null;
      image_contain?: boolean | null;
      farey_order?: string;
      startDate?: Date;
      endDate?: Date | null;
      status?: Status;
      function_en?: string | null;
      function_nl?: string | null;
      linkedin?: string | null;
      orcid?: string | null;
      publications?: API['Publication']['read'][];
      projects?: API['Project']['read'][];
      id?: number;
    };
    update: {
      user?: number | null;
      name?: string;
      firstname?: string;
      text_en?: string;
      text_nl?: string;
      image?: number | null;
      image_contain?: boolean | null;
      farey_order?: string;
      startDate?: Date;
      endDate?: Date | null;
      status?: Status;
      function_en?: string | null;
      function_nl?: string | null;
      linkedin?: string | null;
      orcid?: string | null;
      publications?: number[];
      projects?: number[];
    };
  };

  Log: {
    create: {
      message: string;
      timestamp: Date;
    };
    read: {
      message?: string;
      timestamp?: Date;
      user?: API['User']['read'];
      id?: number;
    };
    update: {
      message?: string;
      timestamp?: Date;
    };
  };

  Folder: {
    delete: {};
    create: {
      folderName: string;
      parentFolder?: number | null;
      subFolders: number[];
      files: number[];
    };
    read: {
      folderName?: string;
      parentFolder?: API['Folder']['read'] | null;
      subFolders?: API['Folder']['read'][];
      files?: API['File']['read'][];
      id?: number;
    };
    update: {
      folderName?: string;
      parentFolder?: number | null;
      subFolders?: number[];
      files?: number[];
    };
  };

  Session: {
    create: {
      Email: string;
      Password: string;
    };
  };

  Account: {
    update: {
      Email?: string;
      Password?: string;
      Token?: string;
      RegistrationCode?: string;
      Action?: string;
    };
  };

  DBLog: {
    delete: {};
    create: {
      table_name: string;
      old_row_data?: any | null;
      new_row_data?: any | null;
      dml_type: DBLogType;
      dml_timestamp: Date;
    };
    read: {
      table_name?: string;
      old_row_data?: any | null;
      new_row_data?: any | null;
      dml_type?: DBLogType;
      dml_timestamp?: Date;
      id?: number;
    };
    update: {
      table_name?: string;
      old_row_data?: any | null;
      new_row_data?: any | null;
      dml_type?: DBLogType;
      dml_timestamp?: Date;
    };
  };

  VerificationCode: {
    delete: {};
    create: {
      userId: number;
      hmac: string;
      salt: string;
      expiresAt: Date;
      attemptsLeft: number;
    };
    read: {
      userId?: number;
      hmac?: string;
      salt?: string;
      expiresAt?: Date;
      attemptsLeft?: number;
      id?: number;
    };
    update: {
      userId?: number;
      hmac?: string;
      salt?: string;
      expiresAt?: Date;
      attemptsLeft?: number;
    };
  };

  Menu: {
    delete: {};
    create: {
      title: string;
      items: number[];
      locale: string;
    };
    read: {
      title?: string;
      items?: API['MenuItem']['read'][];
      locale?: string;
      id?: number;
    };
    update: {
      title?: string;
      items?: number[];
      locale?: string;
    };
  };

  MenuItem: {
    delete: {};
    create: {
      title: string;
      slug: string;
      text: any;
      menu: number;
      farey_order: string;
    };
    read: {
      title?: string;
      slug?: string;
      text?: any;
      menu?: API['Menu']['read'];
      farey_order?: string;
      id?: number;
    };
    update: {
      title?: string;
      slug?: string;
      text?: any;
      menu?: number;
      farey_order?: string;
    };
  };
};
export const ModelName = ['ApplicationLog', 'ApplicationSettings', 'File', 'User', 'Page', 'Section', 'Project', 'Publication', 'Member', 'Log', 'Folder', 'Session', 'Account', 'DBLog', 'VerificationCode', 'Menu', 'MenuItem'] as const; 
 export type ModelName = typeof ModelName[number];